import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { usePlatformVoices, useVoiceModels } from '../hooks/useVoiceModels';
import type { PlatformVoice, VoiceModel } from '../hooks/useVoiceModels';

interface VoiceSelectorProps {
  selectedVoiceId: string | null;
  onSelect: (voiceId: string) => void;
}

function PlatformVoiceCard({
  voice,
  selected,
  onSelect,
}: {
  voice: PlatformVoice;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={`flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors hover:bg-accent/50 w-full ${
        selected ? 'border-primary bg-accent' : 'border-border bg-card'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{voice.name}</span>
        {voice.labels.accent && (
          <Badge variant="secondary" className="text-xs">
            {voice.labels.accent}
          </Badge>
        )}
      </div>
      {voice.description && (
        <span className="text-xs text-muted-foreground">{voice.description}</span>
      )}
    </button>
  );
}

function ClonedVoiceCard({
  model,
  selected,
  onSelect,
}: {
  model: VoiceModel;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={`flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors hover:bg-accent/50 w-full ${
        selected ? 'border-primary bg-accent' : 'border-border bg-card'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{model.name}</span>
        <Badge variant="outline" className="text-xs">
          Custom
        </Badge>
      </div>
      {model.description && (
        <span className="text-xs text-muted-foreground">{model.description}</span>
      )}
    </button>
  );
}

export function VoiceSelector({ selectedVoiceId, onSelect }: VoiceSelectorProps) {
  const { data: platformVoices, isLoading: loadingPlatform } = usePlatformVoices();
  const { data: voiceModels, isLoading: loadingModels } = useVoiceModels();

  return (
    <Tabs defaultValue="platform" className="w-full">
      <TabsList className="w-full">
        <TabsTrigger value="platform" className="flex-1">
          Platform Voices
        </TabsTrigger>
        <TabsTrigger value="custom" className="flex-1">
          My Voices{voiceModels && voiceModels.length > 0 ? ` (${voiceModels.length})` : ''}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="platform">
        {loadingPlatform ? (
          <div className="grid grid-cols-2 gap-2 mt-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 mt-2 max-h-64 overflow-y-auto pr-1">
            {(platformVoices ?? []).map((voice) => (
              <PlatformVoiceCard
                key={voice.voiceId}
                voice={voice}
                selected={selectedVoiceId === voice.voiceId}
                onSelect={() => onSelect(voice.voiceId)}
              />
            ))}
          </div>
        )}
      </TabsContent>

      <TabsContent value="custom">
        {loadingModels ? (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Skeleton className="h-16 rounded-lg" />
            <Skeleton className="h-16 rounded-lg" />
          </div>
        ) : !voiceModels || voiceModels.length === 0 ? (
          <div className="mt-4 text-center text-sm text-muted-foreground py-8">
            No custom voices yet. Create one on the Voice Models tab.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 mt-2 max-h-64 overflow-y-auto pr-1">
            {voiceModels.map((model) => (
              <ClonedVoiceCard
                key={model.voiceModelId}
                model={model}
                selected={selectedVoiceId === model.providerVoiceId}
                onSelect={() => onSelect(model.providerVoiceId)}
              />
            ))}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}
